import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Activity, Bot, ShieldCheck, LineChart, Bitcoin, Cpu, Gauge, Check,
  ArrowRight, Zap, Lock, TrendingUp, Wallet, Coins, CandlestickChart, Headphones,
} from "lucide-react";
import { SupportChat } from "@/components/SupportChat";
import { JoinTeamSection } from "@/components/JoinTeamSection";

const HERO_IMG = "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxzdG9jayUyMG1hcmtldCUyMGNoYXJ0fGVufDB8fHx8MTc4Mzk3MTQ3NHww&ixlib=rb-4.1.0&q=85";
const PREVIEW_IMG = "https://images.unsplash.com/photo-1651341050677-24dba59ce0fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxzdG9jayUyMG1hcmtldCUyMGNoYXJ0fGVufDB8fHx8MTc4Mzk3MTQ3NHww&ixlib=rb-4.1.0&q=85";
const BTC_IMG = "https://images.pexels.com/photos/6770521/pexels-photo-6770521.jpeg";
const LOGO = "/smartfx-logo.png";

function Nav() {
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src={LOGO} alt="Smart FX" className="h-10 w-auto object-contain" />
          <span className="font-heading text-lg font-bold tracking-tight">SMART FX</span>
        </div>
        <nav className="hidden md:flex items-center gap-8 text-sm text-[#888]">
          <a href="#how" className="hover:text-white transition-colors">How it works</a>
          <a href="#features" className="hover:text-white transition-colors">Features</a>
          <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          <a href="#get-started" className="hover:text-white transition-colors">Get Started</a>
          <a href="#payment" className="hover:text-white transition-colors">Payment</a>
        </nav>
        <div className="flex items-center gap-3">
          <button data-testid="nav-support-button" onClick={() => window.dispatchEvent(new Event("open-support"))}
            className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[#ccc] hover:text-white transition-colors">
            <Headphones className="h-4 w-4" /> Sipò
          </button>
          <Link to="/login" data-testid="nav-login-link" className="text-sm text-[#ccc] hover:text-white transition-colors">Sign in</Link>
          <Link to="/register" data-testid="nav-register-link">
            <Button className="h-9 rounded-sm bg-white text-black hover:bg-[#e5e5e5] font-semibold transition-colors">Get Started</Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Trading chart" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-black/75" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/40 to-transparent" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 py-24 w-full">
        <div className="max-w-3xl fade-up">
          <div className="inline-flex items-center gap-2 border border-[#222] bg-[#0F0F0F]/80 px-3 py-1.5 mb-6">
            <span className="h-2 w-2 rounded-full bg-[#00F593] live-dot" />
            <span className="font-mono-term text-xs uppercase tracking-widest text-[#ccc]">AutoTrade Engine</span>
          </div>
          <h1 className="font-heading text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Connect your MT5.<br />Let Smart Scalper trade for you.
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#aaa] max-w-xl leading-relaxed">
            Smart FX provides institutional-grade AutoTrade technology, risk management and full automation.
            Bring your own broker or Prop Firm challenge — we power the trading.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/register" data-testid="hero-cta-button">
              <Button className="h-12 px-7 rounded-sm bg-white text-black hover:bg-[#e5e5e5] font-semibold text-base transition-colors">
                Start Trading <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href="#pricing">
              <Button variant="outline" className="h-12 px-7 rounded-sm border-[#333] bg-transparent text-white hover:bg-[#1A1A1A] transition-colors">
                View Plans
              </Button>
            </a>
          </div>
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
            {[["24/7", "Automation"], ["1.0%", "Risk / trade"], ["MT5", "Native"]].map(([n, l]) => (
              <div key={l}>
                <div className="font-heading text-2xl font-bold text-white">{n}</div>
                <div className="font-mono-term text-xs uppercase tracking-widest text-[#888] mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { icon: Lock, title: "Buy your challenge", desc: "Purchase your own Prop Firm challenge (FTMO, FundedNext, The5ers) or use any MT5 broker. Smart FX does not sell challenges." },
  { icon: Cpu, title: "Connect your account", desc: "Link your MT5 login securely. Your account details are read through MetaTrader connectivity." },
  { icon: Bot, title: "Select your Smart Scalper", desc: "Activate Smart Scalper Fx and configure your risk profile and trading pairs." },
  { icon: Zap, title: "Press Start", desc: "The Smart Scalper analyses the market, executes strategy and manages every trade automatically." },
];

function HowItWorks() {
  return (
    <section id="how" className="max-w-7xl mx-auto px-6 py-24">
      <div className="max-w-2xl mb-14">
        <span className="font-mono-term text-xs uppercase tracking-widest text-[#00F593]">How it works</span>
        <h2 className="font-heading text-4xl font-bold tracking-tight mt-3">Four steps to automated trading</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-px bg-[#222] border border-[#222]">
        {STEPS.map((s, i) => (
          <div key={s.title} className="bg-[#0A0A0A] p-8 group hover:bg-[#111] transition-colors">
            <div className="flex items-center justify-between mb-6">
              <s.icon className="h-6 w-6 text-white" />
              <span className="font-mono-term text-xs text-[#444]">0{i + 1}</span>
            </div>
            <h3 className="font-heading text-lg font-semibold mb-2">{s.title}</h3>
            <p className="text-sm text-[#888] leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const FEATURES = [
  { icon: Bot, title: "Smart Scalper Engine", desc: "Adaptive scalping engine trained on order-flow and liquidity structure." },
  { icon: ShieldCheck, title: "Prop Firm Safety Mode", desc: "Respects daily loss limits and maximum drawdown to protect funded accounts." },
  { icon: Gauge, title: "Advanced Risk Management", desc: "Automatic SL/TP, trailing stops and break-even protection on every position." },
  { icon: Activity, title: "Live Trading Activity", desc: "Watch the engine's reasoning in real time — analysis, entries, and trade management." },
  { icon: TrendingUp, title: "Smart Scalper Strategy", desc: "One precision scalping strategy (RSI + Bollinger + EMA), synced across Signal, MT5 & Prop Firm." },
  { icon: LineChart, title: "Full AutoTrade Dashboard", desc: "Start/stop control, robot list, quotes and account monitoring in one place." },
];

function Features() {
  return (
    <section id="features" className="border-y border-[#222] bg-[#080808]">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="font-mono-term text-xs uppercase tracking-widest text-[#00F593]">Platform</span>
            <h2 className="font-heading text-4xl font-bold tracking-tight mt-3 mb-10">Everything you need to trade on autopilot</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {FEATURES.map((f) => (
                <div key={f.title}>
                  <f.icon className="h-6 w-6 text-white mb-3" />
                  <h3 className="font-heading font-semibold mb-1">{f.title}</h3>
                  <p className="text-sm text-[#888] leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="border border-[#222] overflow-hidden">
              <img src={PREVIEW_IMG} alt="trading interface" className="w-full h-[520px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-[#0F0F0F] border border-[#222] p-4 w-56 hidden sm:block">
              <div className="flex items-center gap-2 mb-2">
                <span className="h-2 w-2 rounded-full bg-[#00F593] live-dot" />
                <span className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Live</span>
              </div>
              <div className="font-mono-term text-sm text-white">BUY XAUUSD</div>
              <div className="font-mono-term text-xs text-[#00F593] mt-1">+1.82R target</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PlanCard({ plan }) {
  return (
    <div data-testid={`plan-card-${plan.id}`} className={`relative bg-[#0F0F0F] border p-7 flex flex-col transition-colors ${plan.popular ? "border-white" : "border-[#222] hover:border-[#444]"}`}>
      {plan.popular && (
        <span className="absolute -top-3 left-7 bg-white text-black text-xs font-semibold px-3 py-1 font-mono-term uppercase tracking-widest">Popular</span>
      )}
      <div className="flex items-baseline justify-between">
        <h3 className="font-heading text-lg font-semibold">{plan.name}</h3>
        <span className="font-mono-term text-xs text-[#888] uppercase">{plan.days}d</span>
      </div>
      <p className="text-sm text-[#888] mt-1">{plan.tagline}</p>
      <div className="mt-5 mb-6">
        <span className="font-heading text-4xl font-bold">${plan.price}</span>
        <span className="text-[#888] text-sm"> / {plan.days} days</span>
      </div>
      <ul className="space-y-2.5 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-[#ccc]">
            <Check className="h-4 w-4 text-[#00F593] mt-0.5 shrink-0" /> {f}
          </li>
        ))}
      </ul>
      <Link to="/register" className="mt-6">
        <Button className={`w-full h-11 rounded-sm font-semibold transition-colors ${plan.popular ? "bg-white text-black hover:bg-[#e5e5e5]" : "bg-[#1A1A1A] text-white hover:bg-[#252525] border border-[#333]"}`}>
          Choose {plan.name.split(" ").pop()}
        </Button>
      </Link>
    </div>
  );
}

const BALANCE_TIERS = [
  { id: "test", icon: Wallet, tag: "Test / Beginner", amount: "$50 – $100",
    desc: "0.01 lot · 1 pair · 1–2 trades per pair. Learn the system with minimal risk." },
  { id: "standard", icon: Coins, tag: "Standard", amount: "$300 – $500",
    desc: "0.01–0.02 lot · 2 pairs · 2–3 trades per pair. Balanced automated scalping." },
  { id: "comfortable", icon: TrendingUp, tag: "Comfortable", amount: "$1,000+",
    desc: "0.04+ lot · 3 pairs · up to 3 trades per pair. Full engine capacity." },
  { id: "propfirm", icon: ShieldCheck, tag: "Prop Firm (Funded)", amount: "Challenge fee only",
    desc: "No large personal capital needed — trade a funded account. Prop Firm Safety Mode protects your limits." },
];

function StartingBalance() {
  return (
    <section id="get-started" className="border-t border-[#222] bg-[#080808]" data-testid="starting-balance-section">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-14">
          <span className="font-mono-term text-xs uppercase tracking-widest text-[#00F593]">Before you start</span>
          <h2 className="font-heading text-4xl font-bold tracking-tight mt-3">How much to start with</h2>
          <p className="text-[#888] mt-3">
            Smart FX powers Smart Scalper — you fund your own MT5 or Prop Firm account. Here's the balance we
            recommend so the Smart Scalper can trade comfortably without over-risking. This is guidance only, not
            financial advice.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-px bg-[#222] border border-[#222]">
          {BALANCE_TIERS.map((t) => (
            <div key={t.id} data-testid={`balance-tier-${t.id}`} className="bg-[#0A0A0A] p-8 hover:bg-[#111] transition-colors">
              <t.icon className="h-6 w-6 text-white mb-6" />
              <div className="font-mono-term text-xs uppercase tracking-widest text-[#888]">{t.tag}</div>
              <div className="font-heading text-2xl font-bold text-white mt-1 mb-3">{t.amount}</div>
              <p className="text-sm text-[#888] leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#666] mt-6">
          Minimum to start: <span className="text-[#ccc]">$100</span> (0.01 lot, 1 pair). Ideal:
          <span className="text-[#ccc]"> $500–$1,000</span> or a funded Prop Firm account.
        </p>

        <div className="mt-10 border border-[#222] bg-[#0F0F0F] p-6 flex items-start gap-4" data-testid="binary-options-callout">
          <div className="h-11 w-11 border border-[#333] flex items-center justify-center shrink-0">
            <CandlestickChart className="h-5 w-5 text-[#00F593]" />
          </div>
          <div>
            <h3 className="font-heading font-semibold">Binary Options included</h3>
            <p className="text-sm text-[#888] mt-1 leading-relaxed">
              Prefer binary trading? Smart FX also generates Binary Options signals (direction,
              confidence &amp; expiry) — available anytime inside the <span className="text-[#ccc]">app Settings</span> once
              you're signed in.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pricing({ plans }) {
  const sorted = [...plans].sort((a, b) => (a.days || 0) - (b.days || 0));
  return (
    <section id="pricing" className="max-w-7xl mx-auto px-6 py-24">
      <div className="max-w-2xl mb-14">
        <span className="font-mono-term text-xs uppercase tracking-widest text-[#00F593]">Pricing</span>
        <h2 className="font-heading text-4xl font-bold tracking-tight mt-3">Pay for the technology, keep your account</h2>
        <p className="text-[#888] mt-3">Smart FX provides the technology. You connect your own MT5 or Prop Firm account.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pricing-grid">
        {sorted.map((p) => <PlanCard key={p.id} plan={p} />)}
      </div>
    </section>
  );
}

function Payment() {
  return (
    <section id="payment" className="border-t border-[#222] bg-[#080808]">
      <div className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <span className="font-mono-term text-xs uppercase tracking-widest text-[#00F593]">Crypto Payment</span>
          <h2 className="font-heading text-4xl font-bold tracking-tight mt-3 mb-5">Pay securely with Bitcoin</h2>
          <p className="text-[#888] leading-relaxed mb-6">
            Choose a plan, send payment to the provided Bitcoin address, then upload your payment
            screenshot. Once our team verifies the transaction, your AutoTrade access is activated instantly.
          </p>
          <ol className="space-y-4">
            {["Register and pick your plan", "Send BTC to the wallet address shown at checkout", "Upload your payment proof screenshot", "Access is approved and AutoTrade unlocks"].map((s, i) => (
              <li key={s} className="flex items-center gap-4">
                <span className="h-8 w-8 border border-[#333] flex items-center justify-center font-mono-term text-sm shrink-0">{i + 1}</span>
                <span className="text-sm text-[#ccc]">{s}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="border border-[#222] overflow-hidden relative">
          <img src={BTC_IMG} alt="Bitcoin payment" className="w-full h-[420px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          <div className="absolute bottom-6 left-6 flex items-center gap-2">
            <Bitcoin className="h-5 w-5 text-[#F7931A]" />
            <span className="font-mono-term text-sm uppercase tracking-widest">Bitcoin accepted</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  return (
    <div className="bg-[#050505] grain min-h-screen">
      <Nav />
      <Hero />
      <HowItWorks />
      <Features />
      <StartingBalance />
      <Pricing plans={plans} />
      <Payment />
      <JoinTeamSection />
      <footer className="border-t border-[#222]">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={LOGO} alt="Smart FX" className="h-8 w-auto object-contain" />
            <span className="font-heading font-bold tracking-tight">SMART FX</span>
          </div>
          <p className="text-xs text-[#666]">Smart FX provides trading technology only. Trading involves risk. © 2026 Smart FX AutoTrade.</p>
        </div>
      </footer>
      <SupportChat />
    </div>
  );
}
